import { SalePhase } from 'config/constants/vvsMinerMole/misc'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useVvs, useVvsMinerMoleContract, useVvsMinerMoleNurseryContract } from 'hooks/useContract'
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import { BigNumber as EtherBigNumber, ContractReceipt } from 'ethers'
import { isNilOrUndef } from 'utils/isNilOrUndef'
import tokens from 'config/constants/tokens'
import { useCallback, useMemo } from 'react'
import BigNumber from 'bignumber.js'
import useToast from 'hooks/useToast'
import { useTranslation } from 'contexts/Localization'
import { useTransactionAdder } from 'state/transactions/hooks'
import { QueryKey } from 'config/constants/queryKey'
import { getGeneratedMerkleProof } from 'config/constants/vvsMinerMole/merkleProof'
import { VvsMinerMole } from '../../../types/ethers-contracts'
import { IVVSMinerMoleNursery } from '../../../types/ethers-contracts/VvsMinerMoleNursery'

type DecimalAmount = string // numbers that are not divided by 1e18 yet
const transformSalePhaseInfo = (info: IVVSMinerMoleNursery.SaleStructOutput) => {
  return {
    ...info,
    maxAdoptableMinerMoles: info.maxAdoptableMinerMoles.toNumber(),
    maxAdoptableMinerMolesPerAddress: info.maxAdoptableMinerMolesPerAddress.toNumber(),
    durationInSec: info.durationInSec.toNumber(),
  } as Omit<IVVSMinerMoleNursery.SaleStructOutput, 'maxAdoptableMinerMoles' | 'maxAdoptableMinerMolesPerAddress'> & {
    maxAdoptableMinerMoles: number
    maxAdoptableMinerMolesPerAddress: number
    durationInSec: number
  }
}

const mapSalePhase = (n: number) =>
  ({
    0: SalePhase.WHITELIST,
    1: SalePhase.PRESALE,
    2: SalePhase.PUBLIC,
  }[n])

export const useMaxSupply = (queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>) => {
  const contract = useVvsMinerMoleContract()
  return useQuery(
    ['mole-contract:max-supply'],
    async () => {
      const bigNum = await contract.MAX_MINER_MOLE_SUPPLY()
      return bigNum.toNumber()
    },
    {
      initialData: 10000,
      ...queryOptions,
    },
  )
}

export const useCurrentSalePhase = (queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>) => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  return useQuery(
    [QueryKey.VVS_MOLE_CURRENT_SALE_PHASE],
    async () => {
      const phaseNo = await nurseryContract.getCurrentSalePhrase()

      return mapSalePhase(phaseNo)
    },
    queryOptions,
  )
}

export const useSalePhaseInfo = (
  phase: SalePhase,
  queryOptions?: Omit<
    UseQueryOptions<
      ReturnType<typeof transformSalePhaseInfo>,
      unknown,
      ReturnType<typeof transformSalePhaseInfo>,
      [string, SalePhase]
    >,
    'queryFn' | 'queryKey'
  >,
) => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  return useQuery(
    ['mole-contract:phase-info', phase],
    async () => {
      if (isNilOrUndef(phase)) return null
      const res = await nurseryContract.getSale(phase)

      return transformSalePhaseInfo(res)
    },
    {
      ...queryOptions,
    },
  )
}

export const useIsWhitelisted = (
  phase: SalePhase,
  queryOptions?: Omit<UseQueryOptions<boolean, unknown, boolean, any[]>, 'queryFn' | 'queryKey'>,
) => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  const { account } = useActiveWeb3React()
  return useQuery(
    ['mole-contract:is-whitelisted', account, phase],
    async () => {
      if (!account || isNilOrUndef(phase)) return false

      const proof = getGeneratedMerkleProof(account, phase)

      const res = await nurseryContract.isWhitelistedAddress(account, proof, EtherBigNumber.from(phase))
      return res
    },
    queryOptions,
  )
}

/**
 *
 * @returns minted mole amount
 */
export const useMoleSupply = (
  queryOptions?: Omit<UseQueryOptions<number, unknown, number, string[]>, 'queryFn' | 'queryKey'>,
) => {
  const contract = useVvsMinerMoleContract()

  return useQuery(
    ['mole-contract:current-supply'],
    async () => {
      const res = await contract.totalSupply()
      return res?.toNumber()
    },
    {
      refetchInterval: 5000,
      ...queryOptions,
    },
  )
}

export const useMintMole = () => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  const { account } = useActiveWeb3React()
  return useMutation(async (params: { phase: SalePhase; amount: number }): Promise<ContractReceipt> => {
    const { phase, amount } = params
    const proof = getGeneratedMerkleProof(account, phase)

    const tx = await nurseryContract.adoptMinerMoles(amount, proof)
    const receipt = await tx.wait()
    return receipt
  })
}
export const useUserMintedTokens = () => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  const { account } = useActiveWeb3React()

  return useQuery([QueryKey.VVS_MOLE_USER_TOKENS, account], async () => {
    if (!account) return []
    const res = await nurseryContract.getUserMintedTokens(account)
    return res
  })
}
export const useUserMintedTokensLength = () => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  const { account } = useActiveWeb3React()

  return useQuery([QueryKey.VVS_MOLE_USER_TOKEN_LENGTH, account], async () => {
    if (!account) return 0
    const res = await nurseryContract.userMintedTokensLength(account)
    return res.toNumber()
  })
}
export const useMintAllowance = () => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  const { account } = useActiveWeb3React()
  const vvsContract = useVvs()

  return useQuery(['vvs-allowance:vvs-mole-nursery', account, nurseryContract.address], async () => {
    if (isNilOrUndef(account) || isNilOrUndef(nurseryContract.address)) return new BigNumber(0)
    const res = await vvsContract.allowance(account, nurseryContract.address)

    return new BigNumber(res.toString())
  })
}
export const useMintApproval = () => {
  const { toastError, toastSuccess } = useToast()
  const { t } = useTranslation()
  const nurseryContract = useVvsMinerMoleNurseryContract()
  const queryClient = useQueryClient()
  const vvsContract = useVvs()

  // disable 'unused' rule here to hide unwanted mutate function from consumer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutateAsync, mutate, data, error, ...rest } = useMutation(
    async (amount: DecimalAmount) => {
      const tx = await vvsContract.approve(nurseryContract.address, amount)
      const receipt = await tx.wait()

      return receipt
    },
    {
      onSuccess: () => {
        // trigger fetch new allowance update, the allowance update is not real time, so this is not reliable
        queryClient.invalidateQueries('vvs-allowance:vvs-mole-nursery')
      },
    },
  )

  const isApproved = useMemo(() => {
    return !!(data && !error)
  }, [data, error])

  const approve = useCallback(
    async (amount: DecimalAmount) => {
      try {
        await mutateAsync(amount)
        toastSuccess(
          t('Transaction Receipt'),
          t('Approve %token%', {
            token: tokens.vvs.symbol,
          }),
        )
        return true
      } catch (e) {
        toastError(t('Transaction Failed'), t('Please try again later'))
        return false
      }
    },
    [mutateAsync, t, toastError, toastSuccess],
  )
  return {
    isApproved,
    approve,
    ...rest,
  }
}

export const useAdoptedMinerMoles = (salePhase: SalePhase) => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  return useQuery(['mole-contract:adopted-moles', salePhase], async () => {
    const nums = await nurseryContract.numOfAdoptedMinerMoles(salePhase)

    return new BigNumber(nums.toString())
  })
}

export const useIsSaleActive = (salePhase: SalePhase, queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>) => {
  const nurseryContract = useVvsMinerMoleNurseryContract()
  return useQuery([QueryKey.VVS_MOLE_IS_SALE_ACTIVE, salePhase], () => nurseryContract.isSaleActive(salePhase), {
    ...queryOptions,
    enabled: (isNilOrUndef(queryOptions?.enabled) ? true : queryOptions?.enabled) && !isNilOrUndef(salePhase),
  })
}
export const queryKeyBalanceOfUser = (account: string) => [QueryKey.BALANCE_OF_USER, account]
export const queryBalanceOfUser = async (contract: VvsMinerMole, account: string) => {
  if (!account) return 0
  const res = await contract.balanceOf(account)
  return res.toNumber()
}
export const useBalanceOfUser = (queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>) => {
  const contract = useVvsMinerMoleContract()
  const { account } = useActiveWeb3React()

  return useQuery(queryKeyBalanceOfUser(account), () => queryBalanceOfUser(contract, account), {
    enabled: !!account && (queryOptions?.enabled ?? true),
    ...queryOptions,
  })
}

export const useTokenOfOwnerByIndex = (balanceOfUser, index) => {
  const contract = useVvsMinerMoleContract()
  const { account } = useActiveWeb3React()

  return useQuery([QueryKey.TOKEN_OF_USER_BY_INDEX, account, index, balanceOfUser], async () => {
    if (!account || isNilOrUndef(index) || !balanceOfUser || balanceOfUser === 0) return null
    const res = await contract.tokenOfOwnerByIndex(account, index)
    return res.toString()
  })
}

export const useGetApprovalForAll = (operatorAddress: string) => {
  const contract = useVvsMinerMoleContract()

  return useQuery([QueryKey.VVS_MOLE_APPROVAL_FOR_ALL, operatorAddress], async () => {
    if (isNilOrUndef(operatorAddress)) return null
    const res = await contract.setApprovalForAll(operatorAddress, true)
    return res
  })
}

export const useIsApprovalForAll = (
  operatorAddress: string,
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const contract = useVvsMinerMoleContract()
  const { account } = useActiveWeb3React()

  return useQuery(
    [QueryKey.VVS_MOLE_APPROVAL_IS_FOR_ALL, account],
    async () => {
      if (!account) return null
      const res = await contract.isApprovedForAll(account, operatorAddress)
      return res
    },
    {
      enabled: !!operatorAddress && !!account && (queryOptions?.enabled ?? true),
      ...queryOptions,
    },
  )
}

export const useNFTStakingApproval = () => {
  const { toastError } = useToast()
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const contract = useVvsMinerMoleContract()
  const addTransaction = useTransactionAdder()

  // disable 'unused' rule here to hide unwanted mutate function from consumer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutateAsync, mutate, data, error, ...rest } = useMutation(
    async (operatorAddress: string) => {
      const tx = await contract.setApprovalForAll(operatorAddress, true)
      const receipt = await tx.wait()

      return { tx, receipt }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.VVS_MOLE_APPROVAL_IS_FOR_ALL)
        queryClient.invalidateQueries(QueryKey.BALANCE_OF_USER)
        queryClient.invalidateQueries(QueryKey.FARM_BOOST_USER_INFO)
        queryClient.invalidateQueries(QueryKey.VVS_MOLE_TOKENS_OF_USER)
      },
    },
  )

  const isApproved = useMemo(() => {
    return !!(data && !error)
  }, [data, error])

  const approve = useCallback(
    async (operatorAddress: string) => {
      try {
        const { tx } = await mutateAsync(operatorAddress)
        addTransaction(tx, {
          title: t('Approve NFT staking Completed'),
          summary: t('Approve NFT staking'),
        })
        return true
      } catch (e) {
        toastError(t('Transaction Failed'), t('Please try again later'))
        return false
      }
    },
    [mutateAsync, t, addTransaction, toastError],
  )

  return {
    isApproved,
    approve,
    ...rest,
  }
}
